/* eslint-disable no-nested-ternary */
/* eslint-disable prefer-destructuring */
import { useOktaAuth } from "@okta/okta-react";
import {
  createContext,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation } from "react-router";
import { axios } from "../../api";
import TechnicalError from "../../components/ErrorPage/TechnicalError/TechnicalError";
import LoadingIndicator from "../../components/LoadingIndicator/LoadingIndicator";
import i18n from "../../i18n/i18n";
import LocalStorage from "../../utils/LocalStorage";
import { saveDisplayMyPromoLink } from "../../utils/promotions/promotionUtils";
import { getAccountIdFromUrl } from "../../utils/urlFormatters";
import { ConfigContext } from "../ConfigContext";

export interface BusinessFeatureToggle {
  HIDE_PROMOTIONS: boolean;
}

export const FeatureToggleContext = createContext<BusinessFeatureToggle>({
  HIDE_PROMOTIONS: true,
});

const COUNTRY_WITH_NO_PROMOTIONS = ["IN", "CN", "US"];

const hidePromotions = (dealerLocation: string): boolean =>
  COUNTRY_WITH_NO_PROMOTIONS.includes(dealerLocation);

export const FeatureToggleProvider = ({
  children,
}: {
  children: ReactElement;
}) => {
  const { DEALER_SERVICE_URL } = useContext(ConfigContext);
  const { authState } = useOktaAuth();
  const { pathname, search } = useLocation();

  const [showTechnicalError, setShowTechnicalError] = useState<boolean>(false);

  const [featureToggle, setFeatureToggle] = useState<
    BusinessFeatureToggle | undefined
  >(undefined);

  const getDealerDetails = () => {
    if (
      authState &&
      authState.isAuthenticated &&
      authState.accessToken?.accessToken
    ) {
      const accountId = getAccountIdFromUrl(pathname, search);
      const { accessToken } = authState.accessToken;
      const { dealerAccountId } = LocalStorage.getJSONValue("dealerDetails");

      if (accountId === undefined) setFeatureToggle({ HIDE_PROMOTIONS: true });
      else if (accountId !== dealerAccountId) {
        axios
          .getWithRetries(
            `${DEALER_SERVICE_URL}/api/v1/dealer/accounts?accountIds=${accountId}&lang=${i18n.language}`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          )
          .then((response: any) => {
            LocalStorage.setJSONValue("dealerDetails", {
              dealerLocation: response.data[0].location?.country ?? "BR",
              dealerAccountId: response.data[0].id,
              dealerName: response.data[0].name,
            });
            const showPromotionsTab = !hidePromotions(
              response.data[0].location?.country ?? "BR"
            );
            saveDisplayMyPromoLink(showPromotionsTab);
            setFeatureToggle({ HIDE_PROMOTIONS: showPromotionsTab });
          })
          .catch((error: any) => {
            if (error?.response?.status !== 400) setShowTechnicalError(true);
            else setFeatureToggle({ HIDE_PROMOTIONS: true });
          });
      } else setFeatureToggle({ HIDE_PROMOTIONS: true });
    }
  };
  useEffect(() => {
    getDealerDetails();
  }, [authState]);

  if (showTechnicalError) {
    return <TechnicalError reloadFn={getDealerDetails} />;
  }

  if (featureToggle === undefined) {
    return <LoadingIndicator color="#367c2b" />;
  }

  return (
    <FeatureToggleContext.Provider value={featureToggle}>
      {children}
    </FeatureToggleContext.Provider>
  );
};
